import { lazy, Suspense, useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useSampleResult } from "@/hooks/use-sample-results";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useSamplingEvent } from "@/hooks/use-sampling-events";
import { useLabs } from "@/hooks/use-labs";
import { useAnalysisMethods } from "@/hooks/use-analysis-methods";
import { useSampleResultCustodyTimeline } from "@/hooks/use-sample-result-custody";
import { useToast } from "@/hooks/use-toast";
import { formatDateOnly } from "@/lib/dates";
import { buildCustodyPdfFilename, downloadCustodyPdf } from "@/lib/download-pdf";
import {
  PageHeader,
  PageSkeleton,
  Breadcrumbs,
  EmptyState,
  Button,
  Card,
  CardContent,
} from "@/components/ui";
import { CustodyTimeline } from "@/components/sampling/custody-timeline";
import { SampleResultSummaryCard } from "@/components/sampling/sample-result-summary-card";
import { SampleResultOverviewTab } from "@/components/sampling/sample-result-overview-tab";

// Project convention is named exports; React.lazy expects the module's
// default export. Inline the adapter here so the dynamic import produces a
// `{ default }` shape without changing the modal's source.
const RerouteModal = lazy(() =>
  import("@/components/labs/reroute-modal").then((m) => ({ default: m.RerouteModal })),
);

// Custody statuses that hide the Reroute button — once superseded or
// cancelled, reroute is a no-op and must not be offered.
const REROUTE_HIDDEN_STATUSES = new Set<string>(["superseded", "cancelled"]);

export const Route = createFileRoute("/_authenticated/sample-results/$resultId/")({
  component: SampleResultDetailPage,
});

type DetailTab = "overview" | "custody";

function SampleResultDetailPage() {
  const { t } = useTranslation(["sampleResultDetail", "common", "custody"]);
  const { resultId } = Route.useParams();
  const orgId = useAppStore((s) => s.activeOrgId);
  const userRole = useAppStore((s) => s.user?.role ?? null);
  const isSuperAdmin = userRole === "super_admin";

  const [activeTab, setActiveTab] = useState<DetailTab>("overview");
  const [rerouteOpen, setRerouteOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const toast = useToast();
  const authToken = useAppStore((s) => s.token);

  const { data: sampleResult, isLoading, isError } = useSampleResult(orgId, resultId);
  useRecentPageTitle(sampleResult?.parameter_name ?? sampleResult?.parameter_code ?? undefined);

  // Download PDF button onClick — fetches the custody PDF via the
  // auth-bearing blob helper and triggers a browser "Save as" prompt. On
  // failure, shows the `download_pdf_failed` toast. Wrapped in useCallback
  // so the Button's onClick ref is stable across renders (no infinite
  // loops if wrapped in memoized components downstream).
  const handleDownloadPdf = useCallback(async () => {
    if (!orgId || !authToken) return;
    setPdfDownloading(true);
    try {
      await downloadCustodyPdf({
        orgId,
        resultId,
        token: authToken,
        filename: buildCustodyPdfFilename(new Date()),
      });
    } catch {
      toast.error("sampleResultDetail:error.download_pdf_failed");
    } finally {
      setPdfDownloading(false);
    }
  }, [orgId, resultId, authToken, toast]);

  // Overview tab: resolve current_lab + current_method id → name via the
  // same org-scoped list hooks the timeline uses (shared TanStack Query cache
  // — no extra network round-trip per detail load).
  const { data: labsData } = useLabs(orgId);
  const { data: methodsData } = useAnalysisMethods();

  // Parent sampling_event — needed for collected_date on the summary card.
  const { data: samplingEvent } = useSamplingEvent(orgId, sampleResult?.sampling_event_id ?? "");

  // Custody event count for the summary card. Safe to fire alongside the
  // Custody-tab query — TanStack Query dedupes on the shared key.
  const { data: custodyEvents } = useSampleResultCustodyTimeline(
    orgId,
    resultId,
    isSuperAdmin ? { gcTime: 0 } : undefined,
  );

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !sampleResult) {
    return (
      <div className="p-4 sm:p-8">
        <Breadcrumbs
          items={[{ label: t("title", { ns: "sampleResultDetail" }), to: "/sample-results" }]}
        />
        <div className="mt-6">
          <EmptyState
            title={t("error.not_found", { ns: "sampleResultDetail" })}
            description={t("error.load_failed", { ns: "sampleResultDetail" })}
          />
        </div>
      </div>
    );
  }

  const analyzedDate = sampleResult.analyzed_date ?? sampleResult.created_at;
  const datePart = formatDateOnly(analyzedDate);
  const paramPart =
    sampleResult.parameter_name ?? sampleResult.parameter_code ?? resultId.slice(0, 8);
  const headerTitle = `${paramPart} @ ${datePart}`;

  const currentLab = sampleResult.lab_id
    ? (labsData?.items ?? []).find((l) => l.id === sampleResult.lab_id)
    : undefined;
  const currentLabName = currentLab?.name ?? null;
  const currentLabStatus = currentLab?.status ?? null;
  const currentMethodName =
    sampleResult.method_id &&
    (methodsData?.analysis_methods ?? []).find((m) => m.id === sampleResult.method_id)?.name;

  return (
    <div className="p-4 sm:p-8">
      <Breadcrumbs
        items={[
          { label: t("title", { ns: "sampleResultDetail" }), to: "/sample-results" },
          { label: headerTitle },
        ]}
      />

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <PageHeader title={headerTitle} />
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleDownloadPdf}
            loading={pdfDownloading}
            disabled={pdfDownloading || !authToken}
          >
            {pdfDownloading
              ? t("action.download_pdf_generating", { ns: "sampleResultDetail" })
              : t("action.download_pdf", { ns: "sampleResultDetail" })}
          </Button>
          {!REROUTE_HIDDEN_STATUSES.has(sampleResult.custody_status ?? "") && (
            <Button variant="primary" onClick={() => setRerouteOpen(true)}>
              {t("action.reroute", { ns: "sampleResultDetail" })}
            </Button>
          )}
        </div>
      </div>

      {rerouteOpen && (
        <Suspense fallback={null}>
          <RerouteModal
            orgId={orgId}
            resultId={resultId}
            samplingEventId={sampleResult.sampling_event_id ?? undefined}
            open={rerouteOpen}
            onClose={() => setRerouteOpen(false)}
            onSuccess={() => setRerouteOpen(false)}
          />
        </Suspense>
      )}

      {/* Summary snapshot card — current_* rollup above the tabs */}
      <div className="mt-6">
        <SampleResultSummaryCard
          sampleResult={sampleResult}
          collectedDate={samplingEvent?.collected_date ?? null}
          currentLabName={currentLabName}
          currentLabStatus={currentLabStatus}
          currentMethodName={currentMethodName || null}
          eventCount={custodyEvents?.length}
        />
      </div>

      {/* Tabs */}
      <div className="mt-6 flex border-b border-gray-200 dark:border-gray-700">
        <TabButton active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>
          {t("tab.overview", { ns: "sampleResultDetail" })}
        </TabButton>
        <TabButton active={activeTab === "custody"} onClick={() => setActiveTab("custody")}>
          {t("tab.custody", { ns: "sampleResultDetail" })}
        </TabButton>
      </div>

      <div className="mt-6">
        {activeTab === "overview" && (
          <SampleResultOverviewTab
            sampleResult={sampleResult}
            currentLabName={currentLabName || null}
            currentMethodName={currentMethodName || null}
          />
        )}
        {activeTab === "custody" && (
          <Card>
            <CardContent>
              <CustodyTimeline
                orgId={orgId ?? ""}
                resultId={resultId}
                callerIsSuperAdmin={isSuperAdmin}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? "border-b-2 border-primary text-primary"
          : "text-muted hover:text-on-surface hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600"
      }`}
    >
      {children}
    </button>
  );
}
